import { AuthState } from '@okta/okta-auth-js';
import _isEmpty from 'lodash/isEmpty';
import {
  GENERIC_ERROR,
  MIN_TABLE_PAGE_SIZE,
  ENTERPRISE_PRODUCT_TYPE,
  X_TRACE_ID_RESPONSE_HEADER,
  ENTERPRISE_PRODUCT_NAME,
  DUO_PROVISION_ERROR_CODE,
  HTTP_STATUS_BAD_REQUEST,
} from './constant';
import {
  AUTO_APPLY_ORG_LICENSES_TO_EXISTING_PRODUCT,
  PRODUCT_ALREADY_EXISTS,
} from '../components/overview/Drawers/constants';
import { ProductKeys } from '../enums/Products';
import { UserRoleType } from '../enums/UserRoles';
import { UIUserStatus, UserStatus } from '../enums/UserStatus';
import {
  IProductInstance,
  ISortingOptions,
  IUser,
} from '../interfaces/ICommon';
import {
  IProvisioningMetaData,
  ITransformedReadProductsResponse,
} from '../interfaces/ICustomersLists';
import { IDependentOption, IOption } from '../interfaces/IGroups';
import {
  IBundledRole,
  ICustomRole,
  IRbacRole,
  IRbacUserRole,
  IRole,
  RbacBundledRolesResponseType,
  RbacCustomRolesResponseType,
  RbacRolesResponseType,
} from '../interfaces/IUserRoles';

export const openInNewTab = (url: string): void => {
  window.open(url, '_blank', 'noreferrer');
};

export const capitalizeWord = (word: string): string => {
  const firstLetter = word.charAt(0);
  const firstLetterCap = firstLetter.toUpperCase();
  const remainingLetters = word.slice(1);
  const capitalizedWord = firstLetterCap + remainingLetters.toLowerCase();
  return capitalizedWord;
};

export const getResponseError = (error: any): string => {
  let traceId: string | undefined;
  if (error.response?.headers) {
    traceId = error.response?.headers[X_TRACE_ID_RESPONSE_HEADER];
  }
  return traceId ?? GENERIC_ERROR;
};

export const getResponseErrorStatusCode = (error: any): number => {
  const statusCode = error.response?.status ?? 0;
  const errorCode = error.response?.data?.errorCode;

  // When status is 400, return the errorCode if it exists and equals to DUO_PROVISION_ERROR_CODE
  return statusCode === HTTP_STATUS_BAD_REQUEST &&
    errorCode === DUO_PROVISION_ERROR_CODE
    ? errorCode
    : statusCode;
};

export const toUpperCase = <T extends string>(item: T): string =>
  item.toUpperCase() as Uppercase<T>;

export const composeFunctions = <T>(...functions: T[]): ((input: T) => T) => {
  return (input: T) => {
    return functions.reduce((acc: T, fn: any) => {
      return fn(acc);
    }, input);
  };
};

// If "pageSize" is "0" - returns "1"
// If "pageSize" is not provided - uses "MIN_TABLE_PAGE_SIZE"
export const getTablePaginationPagesCount: (
  itemsLength: number,
  pageSize?: number
) => number = (itemsLength, pageSize = MIN_TABLE_PAGE_SIZE) => {
  if (pageSize === 0) {
    return 1;
  }

  return Math.ceil(itemsLength / pageSize);
};

// If "pageSize" is "0" - returns "false"
// If "pageSize" is not provided - uses "MIN_TABLE_PAGE_SIZE"
export const getTableEnablePagitation: (
  itemsLength: number,
  pageSize?: number
) => boolean = (itemsLength, pageSize = MIN_TABLE_PAGE_SIZE) =>
  getTablePaginationPagesCount(itemsLength, pageSize) > 1;

export const getCombinedRoles = (
  staticRolesResponse: RbacRolesResponseType,
  customRolesResponse: RbacCustomRolesResponseType,
  bundledRolesResponse: RbacBundledRolesResponseType
): IRole[] => {
  const rbacRoles = staticRolesResponse?.data?.rbacRoles || [];
  const customRoles = customRolesResponse?.data?.customRoles || [];
  const bundledRoles = bundledRolesResponse?.data?.bundledRoles || [];
  // Add a `type` property to differentiate between static roles and custom roles.
  const allRoles = [
    ...rbacRoles.map((role: IRbacRole) => ({
      ...role,
      featureFlags: role.featureFlags || [],
      type: UserRoleType.STATIC,
    })),
    ...customRoles.map((role: ICustomRole) => ({
      ...role,
      featureFlags: role.featureFlags ?? [],
      type: UserRoleType.CUSTOM,
      isEditable: isCustomRoleEditable(role),
    })),
    ...bundledRoles.map((role: IBundledRole) => ({
      ...role,
      featureFlags: role.featureFlags ?? [],
      type: UserRoleType.BUNDLED,
      product: ENTERPRISE_PRODUCT_NAME,
      productKey: ENTERPRISE_PRODUCT_TYPE,
    })),
  ] as IRole[];

  return allRoles;
};

export const isCustomRoleEditable = (
  role: ICustomRole | IRbacUserRole
): boolean => {
  return role.productKey !== ProductKeys.SASE;
};

export const getUpdatedProductInstances = (
  instancesOptions: IDependentOption,
  selectedRole: string
): IDependentOption => {
  const currentInstanceOptions = instancesOptions[selectedRole];

  const isSingle = currentInstanceOptions?.length === 1;

  const updateSelectedInstances = currentInstanceOptions?.map(
    (item: IOption) => ({
      ...item,
      selected: isSingle,
      disabled: isSingle,
    })
  );

  const updatedInstances = currentInstanceOptions
    ? {
        ...instancesOptions,
        [selectedRole]: updateSelectedInstances,
      }
    : instancesOptions;

  return updatedInstances;
};

export const sortTableData = <T>(
  tableData: T[],
  sortingOptions?: ISortingOptions[]
): T[] => {
  const firstSortingOption = sortingOptions?.[0];

  if (
    !firstSortingOption ||
    typeof firstSortingOption.id !== 'string' ||
    typeof firstSortingOption.desc !== 'boolean'
  ) {
    return tableData;
  }

  const { id, desc } = firstSortingOption;
  return [...tableData].sort((prevRow, nextRow) => {
    const prevElement = prevRow[id as keyof T] as string;
    const nextElement = nextRow[id as keyof T] as string;

    if (!prevElement || !nextElement) {
      return 0;
    }

    const prevItem = prevElement.toUpperCase();
    const nextItem = nextElement.toUpperCase();

    if (prevItem < nextItem) {
      return desc ? 1 : -1;
    }

    if (prevItem > nextItem) {
      return desc ? -1 : 1;
    }

    return 0;
  });
};

export const getUserId: (authState: AuthState) => string = (authState) => {
  return (authState?.accessToken?.claims?.uid ?? '') as string;
};

export const getSecurityCloud: (authState: AuthState) => string[] = (
  authState
) => {
  return (authState?.accessToken?.claims['security-cloud'] ?? []) as string[];
};

export const getFormateProductNames = (names: string[]): string => {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return names.join(' and ');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

export const getInstanceIdByRoleId = (
  roleId: string,
  allRoles: IRole[],
  allInstances: IProductInstance[]
): string | undefined => {
  const productKey = allRoles.find((role) => role.id === roleId)?.productKey;
  const instanceId = allInstances.find(
    (instance) => instance.productType === productKey
  )?.id;
  return instanceId;
};

const checkEmptyObject = (
  storageKey: string,
  storage: Storage
): string | null => {
  const foundValue = storage.getItem(storageKey);

  if (foundValue && _isEmpty(JSON.parse(foundValue))) return null;

  return foundValue;
};

// SCCv2 uses localStorage and SCCv3 uses sessionStorage for 'okta-token-storage'
export const storageGetItemWrapper = (storageKey: string): string | null => {
  const localStorageValue = checkEmptyObject(storageKey, localStorage);
  const sessionStorageValue = checkEmptyObject(storageKey, sessionStorage);

  return localStorageValue ?? sessionStorageValue;
};

export const isOrgEntitledWithBlockedProvisionCheck = (
  productObject: ITransformedReadProductsResponse
): boolean => {
  // Check if the product is entitled and have blocked provision check with description
  if (productObject?.isOrgEntitled) {
    return (
      (productObject.provisionCheck?.is_blocked &&
        productObject.provisionCheck.type === PRODUCT_ALREADY_EXISTS) ??
      false
    );
  }
  return false;
};

export const isOrgEntitledWithAutoApplyProvisionCheck = (
  productObject: ITransformedReadProductsResponse
): boolean => {
  // Check if the product is entitled and have apply to existing provision check with description in org products
  if (productObject?.isOrgEntitled) {
    return (
      (!productObject.provisionCheck?.is_blocked &&
        productObject.provisionCheck?.type ===
          AUTO_APPLY_ORG_LICENSES_TO_EXISTING_PRODUCT) ??
      false
    );
  }
  return false;
};

export const hasOrgEntitledProvisionProduct = (
  productsTableData: ITransformedReadProductsResponse[]
): boolean => {
  return productsTableData.some((product) => {
    // Check if the product is entitled and has entitlementInstances
    if (
      product.isOrgEntitled &&
      (product.entitlementInstances ?? []).length > 0
    ) {
      // Check if any entitlementInstance satisfies the conditions
      return (product.entitlementInstances ?? []).some((instance) => {
        const hasDescription = instance.entitlement?.description?.trim() !== '';
        const isBlocked = instance.provisionCheck?.is_blocked === true;
        return hasDescription && isBlocked;
      });
    }
    return false;
  });
};

export const triggerRoleChangedTokenRefreshEvent = (
  enterpriseId: string
): void => {
  if (enterpriseId !== '') {
    const event = new CustomEvent('ROLE_CHANGED_TOKEN_REFRESHED', {
      detail: { currentEnterpriseId: enterpriseId },
    });
    window.dispatchEvent(event);
  }
};

export const isNotCreatingNewAccountForCiscoDuo = (
  additionalMetaData: IProvisioningMetaData[]
): boolean => {
  const provisioningItem = additionalMetaData.find(
    (item) => item.name === 'provisioning_selection'
  );
  return provisioningItem?.value !== 'create_new_account';
};

export const filterProductOptionsForNewRow = (
  productsOptions: IOption[],
  selectedProducts: Array<string | null>
): IOption[] => {
  return productsOptions
    .filter((option) => !selectedProducts.includes(option.value))
    .map((option) => ({ ...option, selected: false }));
};

export const getUIUserStatus: (user: IUser) => UIUserStatus = (user) => {
  return UIUserStatus[UserStatus[user.status]];
};
